// The logic of the action plans in the web, with no React: how a plan's state is named and coloured, and what a person may do next. The plan
// and its rules are the API's (routes/v3/_actions/plan.py); this only lays them out and never decides what may run.
// A plan is proposed (nothing queued) or queued (a job's id is on it). Nothing reads the job back, so a plan never moves past queued.

export const STATES = ['proposed', 'queued'];
export const OPEN = ['proposed'];                       // waiting for a person to queue it

const TEXT = { proposed: 'Proposed', queued: 'Queued' };
export const stateText = (state) => TEXT[state] || state;

export const stateClass = (state) => ({ queued: 'pill-green', proposed: 'pill-blue' }[state] || 'pill-gray');

// A finding can get a plan when it is open drift with an action that has a script (the API checks again and says why when it refuses).
export const ACTIONS = ['install', 'update', 'uninstall'];
// The scripted actions that can be run for a finding: its `actions` list, or (a finding kept before they were listed) its one action. A CVE finding
// can offer more than one: update it (when the catalog has a newer version) or remove it.
export const choicesOf = (finding) => {
  if (!finding || finding.state !== 'open' || finding.status !== 'drift') return [];
  return (Array.isArray(finding.actions) ? finding.actions : [finding.action]).filter((a) => ACTIONS.includes(a));
};
export const canPropose = (finding) => choicesOf(finding).length > 0;

// "Update", "Remove", "Install": what a button for the action says.
export const actionLabel = (action) => ({ install: 'Install', update: 'Update', uninstall: 'Remove' }[action] || action);

// What a person can queue for a plan now: {test, apply}. Each kind is queued once; the other kind can still be queued after it.
export const options = (plan) => {
  if (!plan) return { test: false, apply: false };
  const steps = plan.steps || {};
  return { test: !steps.test, apply: !steps.apply };
};

// The ids of the findings a plan is for: one plan serves the same action on the same program of the same machine (a program that is out of date
// and has CVEs is one update). A plan kept from when there was one for each finding has just findingId.
export const findingIdsOf = (plan) => (Array.isArray(plan.findingIds) && plan.findingIds.length ? plan.findingIds : [plan.findingId]);

// "test: job 1793302700-b, apply: job ...", for the list.
export const stepsText = (plan) => ['test', 'apply']
  .filter((k) => plan.steps && plan.steps[k])
  .map((k) => `${k}: job ${plan.steps[k].jobId}`)
  .join(', ');

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The sentence after "create actions", from POST v3/actions/plan's answer: what was made and what was left alone and why.
export const planAllText = (result) => {
  if (!result) return '';
  const made = (result.created || []).length;
  const parts = [`${plural(made, 'plan')} created`];
  const s = result.skipped || {};
  const left = [['already waiting', s.open], ['with a job already queued', s.queued], ['no script action', s.notProposable]].filter(([, n]) => n);
  if (left.length) parts.push(`left alone: ${left.map(([w, n]) => `${n} ${w}`).join(', ')}`);
  return `${parts.join('; ')}. Nothing has been queued.`;
};

// The plans that still have a test or an apply to queue (a bulk action only offers these; the API refuses the rest anyway).
export const readyPlans = (plans) => (plans || []).filter((p) => options(p).test || options(p).apply);
